/**
 * Solicitud de matrícula (detalle) — cabecera + Autorización de Pago DGII +
 * líneas de chasis.
 *
 * La solicitud es el GRUPO (la Autorización de Pago de DGII cubre N placas);
 * los chasis son líneas con estado propio. Pipeline de la cabecera:
 * borrador → solicitada → autorizada → pagada → cerrada (+ cancelada); las
 * líneas van pendiente → recibida (u observada cuando DGII rebota el
 * expediente). Los datos de la autorización se transcriben del documento
 * físico cuando DGII lo emite (por eso son editables después del depósito);
 * en la vía proveedor no existen y el estado autorizada se salta.
 *
 * El cierre nunca es manual: la recepción física (Recibir Matrículas DGII)
 * marca las líneas recibidas y cierra la cabecera al completarse.
 *
 * Se usa embebida en un Dialog fullScreen desde solicitudes.js.
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import { useApi } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

/** Servicios DGII habituales (texto libre con sugerencias). */
const SERVICIOS_DGII = [
  "257 - EMISION PRIMERA PLACA",
  "TRASPASO DE VEHICULO",
  "DUPLICADO DE MATRICULA",
  "DUPLICADO DE PLACA",
];

export const ESTADO_CHIP = {
  borrador: { label: "Borrador", bg: "#ECEFF1", fg: "#455A64" },
  solicitada: { label: "Solicitada", bg: "#FFF3E0", fg: "#E65100" },
  autorizada: { label: "Autorizada", bg: "#E3F2FD", fg: "#1565C0" },
  pagada: { label: "Pagada", bg: "#EDE7F6", fg: "#5E35B1" },
  cerrada: { label: "Cerrada", bg: "#E8F5E9", fg: "#2E7D32" },
  cancelada: { label: "Cancelada", bg: "#FFEBEE", fg: "#C62828" },
};

const LINEA_CHIP = {
  pendiente: { label: "Pendiente", bg: "#FFF3E0", fg: "#E65100" },
  observada: { label: "Observada", bg: "#FFEBEE", fg: "#C62828" },
  recibida: { label: "Recibida", bg: "#E8F5E9", fg: "#2E7D32" },
};

export const chipEstado = (estado, mapa = ESTADO_CHIP) => {
  const c = mapa[estado] || { label: estado, bg: "#ECEFF1", fg: "#455A64" };
  return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />;
};

/** 'dd/mm/aaaa' (respuesta del server) → 'aaaa-mm-dd' (input date). */
const aInputDate = (v) => {
  const p = String(v || "").split("/");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : "";
};

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const filtrarCandidatos = (options, { inputValue }) => {
  const q = inputValue.toLowerCase();
  return options
    .filter((o) =>
      `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.suplidor || ""}`.toLowerCase().includes(q)
    )
    .slice(0, 60);
};

export default function SolicitudMatricula({ id, onExit, onChanged }) {
  const { showAlert } = useGlobalMDAlert();
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;
  const puedeBorrar = puedeAccionPrograma(roleDetalles, "solicitudmatriculas", "borrar");

  const { post } = useApi();
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [solicitud, setSolicitud] = useState(null);
  const [lineas, setLineas] = useState([]);

  // Campos editables de la cabecera.
  const [servicio, setServicio] = useState("");
  const [destino, setDestino] = useState("");
  const [gestor, setGestor] = useState("");
  const [notas, setNotas] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [fechaAutorizacion, setFechaAutorizacion] = useState("");
  const [fechaLimitePago, setFechaLimitePago] = useState("");
  const [impuestoRegistro, setImpuestoRegistro] = useState("");
  const [impuestoCo2, setImpuestoCo2] = useState("");
  const [totalCancelar, setTotalCancelar] = useState("");

  // Vía proveedor: el destino es un selector de los proveedores registrados
  // (catálogo de suplidores de Cuentas x Pagar), nunca texto libre.
  const [proveedorId, setProveedorId] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [proveedoresCargando, setProveedoresCargando] = useState(false);

  // Diálogos.
  const [agregarOpen, setAgregarOpen] = useState(false);
  const [candidatos, setCandidatos] = useState([]);
  const [candidatosCargando, setCandidatosCargando] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [pagoOpen, setPagoOpen] = useState(false);
  const [fechaPago, setFechaPago] = useState("");
  const [referenciaPago, setReferenciaPago] = useState("");
  const [cancelarOpen, setCancelarOpen] = useState(false);
  const [razonCancelar, setRazonCancelar] = useState("");
  const [observarLinea, setObservarLinea] = useState(null);
  const [motivoObservar, setMotivoObservar] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/solicitudmatricula_datos", { id });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo cargar la solicitud", color: "error" });
        return;
      }
      const s = data.solicitud;
      setSolicitud(s);
      setLineas(data.lineas || []);
      setServicio(s.servicio);
      setDestino(s.destino);
      setProveedorId(s.proveedor_id || null);
      setGestor(s.gestor);
      setNotas(s.notas);
      setNumeroDocumento(s.numerodocumento);
      setFechaAutorizacion(aInputDate(s.fechaautorizacion));
      setFechaLimitePago(aInputDate(s.fechalimitepago));
      setImpuestoRegistro(s.impuestoregistro != null ? String(s.impuestoregistro) : "");
      setImpuestoCo2(s.impuestoco2 != null ? String(s.impuestoco2) : "");
      setTotalCancelar(s.totalcancelar != null ? String(s.totalcancelar) : "");
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, post]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Catálogo de proveedores, solo para la vía proveedor.
  useEffect(() => {
    if (!solicitud || solicitud.tipo === "dgii" || proveedores.length) return undefined;
    let activo = true;
    setProveedoresCargando(true);
    post("/api/solicitudmatricula_proveedores", {})
      .then(({ ok, data }) => {
        if (activo && ok && Array.isArray(data.proveedores)) setProveedores(data.proveedores);
      })
      .finally(() => activo && setProveedoresCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitud?.tipo]);

  const cambio = () => {
    cargar();
    if (onChanged) onChanged();
  };

  const ejecutar = async (endpoint, body, exito) => {
    setGuardando(true);
    try {
      const { ok, data } = await post(endpoint, body);
      if (ok) {
        showAlert({ message: data.mensajeok || exito, color: "success" });
        cambio();
        return true;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const guardar = () =>
    ejecutar(
      "/api/solicitudmatricula_guardar",
      {
        id,
        servicio,
        destino,
        proveedor_id: proveedorId,
        gestor,
        notas,
        numerodocumento: numeroDocumento,
        fechaautorizacion: fechaAutorizacion,
        fechalimitepago: fechaLimitePago,
        impuestoregistro: impuestoRegistro,
        impuestoco2: impuestoCo2,
        totalcancelar: totalCancelar,
      },
      "Solicitud actualizada"
    );

  const abrirAgregar = async () => {
    setSeleccion([]);
    setAgregarOpen(true);
    setCandidatosCargando(true);
    try {
      // La vía proveedor lista los vendidos comprados al proveedor de ESTA
      // solicitud: el backend lo toma de la cabecera guardada.
      const { ok, data } = await post("/api/solicitudmatricula_candidatos", {
        tipo: solicitud.tipo,
        id: solicitud.id,
      });
      setCandidatos(ok && Array.isArray(data.candidatos) ? data.candidatos : []);
      if (!ok) {
        showAlert({
          message: data.Error || "No se pudieron cargar los candidatos",
          color: "error",
        });
      }
    } finally {
      setCandidatosCargando(false);
    }
  };

  const agregarChasis = async () => {
    const okAgregar = await ejecutar(
      "/api/solicitudmatricula_agregarchasis",
      { id, chasis: seleccion.map((c) => c.chasis) },
      "Chasis agregados"
    );
    if (okAgregar) setAgregarOpen(false);
  };

  const imprimirActa = async () => {
    const { ok, data } = await post("/api/solicitudmatricula_acta", { id });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo obtener el acta", color: "error" });
      return;
    }
    const s = data.solicitud;
    imprimirListadoGrande({
      compania: data.compania,
      titulo: `Solicitud de Matrículas #${s.id} — ${s.tipo === "dgii" ? "DGII" : "Proveedor"}`,
      subtitulo: [
        s.servicio,
        s.destino && `Destino: ${s.destino}`,
        s.gestor && `Gestor: ${s.gestor}`,
        s.numerodocumento && `Autorización: ${s.numerodocumento}`,
      ]
        .filter(Boolean)
        .join(" · "),
      cabeceras: ["#", "Chasis", "Marca", "Modelo", "Estado", "Placa", "Recibida"],
      filas: (data.lineas || []).map((l, i) => [
        i + 1,
        l.chasis,
        l.marca,
        l.modelo,
        (LINEA_CHIP[l.estado] || { label: l.estado }).label,
        l.placa,
        l.fecharecepcion,
      ]),
    });
  };

  if (cargando && !solicitud) {
    return (
      <Box
        sx={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!solicitud) return null;

  const vivo = ["borrador", "solicitada", "autorizada", "pagada"].includes(solicitud.estado);
  const esDgii = solicitud.tipo === "dgii";
  const columnas = [
    { field: "chasis", headerName: "Chasis", flex: 1.4, minWidth: 170 },
    { field: "marca", headerName: "Marca", flex: 0.8, minWidth: 100 },
    { field: "modelo", headerName: "Modelo", flex: 0.8, minWidth: 100 },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: (p) => chipEstado(p.value, LINEA_CHIP),
    },
    {
      field: "motivoobservacion",
      headerName: "Observación",
      flex: 1,
      minWidth: 140,
    },
    { field: "placa", headerName: "Placa", width: 110 },
    { field: "fecharecepcion", headerName: "Recibida", width: 110 },
    {
      field: "acciones",
      headerName: "",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.4}>
          {vivo && p.row.estado === "pendiente" && solicitud.estado !== "borrador" && (
            <Button
              size="small"
              startIcon={<ReportProblemOutlinedIcon />}
              onClick={() => {
                setObservarLinea(p.row);
                setMotivoObservar("");
              }}
              sx={{ ...btnBase, color: "#C62828", minWidth: 0 }}
            >
              Observar
            </Button>
          )}
          {vivo && p.row.estado === "observada" && (
            <Button
              size="small"
              startIcon={<TaskAltOutlinedIcon />}
              onClick={() =>
                ejecutar(
                  "/api/solicitudmatricula_observar",
                  { linea_id: p.row.id, subsanar: true },
                  "Observación subsanada"
                )
              }
              sx={{ ...btnBase, color: FP.color.success, minWidth: 0 }}
            >
              Subsanar
            </Button>
          )}
          {vivo &&
            puedeBorrar &&
            p.row.estado !== "recibida" &&
            (solicitud.estado === "borrador" || p.row.estado === "observada") && (
              <Button
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() =>
                  ejecutar(
                    "/api/solicitudmatricula_quitarchasis",
                    { linea_id: p.row.id },
                    "Chasis quitado"
                  )
                }
                sx={{ ...btnBase, color: "#C62828", minWidth: 0 }}
              >
                Quitar
              </Button>
            )}
        </Stack>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title={`Solicitud de Matrículas #${solicitud.id}`}
        subtitle={`${esDgii ? "DGII" : "Proveedor"} · creada el ${solicitud.fecha_crea} por ${
          solicitud.user
        }`}
        cta={
          <Stack direction="row" spacing={1.2} alignItems="center">
            {chipEstado(solicitud.estado)}
            {onExit && (
              <Button onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cabecera */}
        <Box sx={sectionBox}>
          <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.accentBlue}>
            Datos de la Solicitud
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={esDgii ? SERVICIOS_DGII : []}
                inputValue={servicio}
                onInputChange={(_, v) => setServicio(v || "")}
                disabled={!vivo}
                renderInput={(params) => (
                  <TextField {...params} label="Servicio / trámite" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {esDgii ? (
                <TextField
                  fullWidth
                  label="Destino (oficina DGII)"
                  value={destino}
                  onChange={(e) => setDestino(e.target.value)}
                  disabled={!vivo}
                  sx={fpInputSx}
                />
              ) : (
                <Autocomplete
                  options={proveedores}
                  loading={proveedoresCargando}
                  disabled={!vivo}
                  // El id es la verdad; destino guarda el nombre para mostrar.
                  value={
                    proveedores.find((p) => p.id === proveedorId) ||
                    (destino ? { id: proveedorId || 0, descrip: destino } : null)
                  }
                  onChange={(_, v) => {
                    setProveedorId(v ? v.id : null);
                    setDestino(v ? v.descrip : "");
                  }}
                  getOptionLabel={(o) => o.descrip || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {o.descrip}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                          {[o.rnc && `RNC ${o.rnc}`, o.telefono].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Proveedor"
                      helperText={
                        !proveedoresCargando && proveedores.length === 0
                          ? "Sin proveedores registrados (se administran en Cuentas x Pagar)"
                          : ""
                      }
                      sx={fpInputSx}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Gestor / responsable"
                value={gestor}
                onChange={(e) => setGestor(e.target.value)}
                disabled={!vivo}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                disabled={!vivo}
                sx={fpInputSx}
              />
            </Grid>
            {solicitud.estado === "cancelada" && solicitud.razoncancelacion && (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.85rem", color: "#C62828", fontWeight: 700 }}>
                  Cancelada: {solicitud.razoncancelacion}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Autorización de pago (vía DGII) */}
        {esDgii && (
          <Box sx={sectionBox}>
            <SectionTitle icon={AccountBalanceOutlinedIcon} color={FP.color.success}>
              Autorización de Pago DGII
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Número del documento"
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                  disabled={!vivo}
                  helperText={
                    solicitud.estado === "solicitada"
                      ? "Al registrarlo la solicitud pasa a Autorizada"
                      : ""
                  }
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha de la autorización"
                  value={fechaAutorizacion}
                  onChange={(e) => setFechaAutorizacion(e.target.value)}
                  disabled={!vivo}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha límite de pago"
                  value={fechaLimitePago}
                  onChange={(e) => setFechaLimitePago(e.target.value)}
                  disabled={!vivo}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  label="Imp. 17% registro (362)"
                  value={impuestoRegistro}
                  onChange={(e) => setImpuestoRegistro(e.target.value)}
                  disabled={!vivo}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  label="Imp. CO2 (396)"
                  value={impuestoCo2}
                  onChange={(e) => setImpuestoCo2(e.target.value)}
                  disabled={!vivo}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField
                  fullWidth
                  label="Total a cancelar"
                  value={totalCancelar}
                  onChange={(e) => setTotalCancelar(e.target.value)}
                  disabled={!vivo}
                  sx={fpInputSx}
                />
              </Grid>
              {solicitud.estado === "pagada" && (
                <Grid item xs={12}>
                  <Chip
                    label={`Pagada el ${solicitud.fechapago}${
                      solicitud.referenciapago ? ` — ${solicitud.referenciapago}` : ""
                    }`}
                    sx={{ bgcolor: "#EDE7F6", color: "#5E35B1", fontWeight: 700 }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Acciones */}
        <Box sx={sectionBox}>
          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
            {vivo && (
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                }
                onClick={guardar}
                disabled={guardando}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Guardar cambios
              </Button>
            )}
            {solicitud.estado === "borrador" && (
              <Button
                startIcon={<SendOutlinedIcon />}
                onClick={() =>
                  ejecutar(
                    "/api/solicitudmatricula_estado",
                    { id, accion: "depositar" },
                    "Solicitud depositada"
                  )
                }
                disabled={guardando || lineas.length === 0}
                sx={btnSolid(FP.color.success, "#2E7D32")}
              >
                Depositar solicitud
              </Button>
            )}
            {["solicitada", "autorizada"].includes(solicitud.estado) && (
              <Button
                startIcon={<PaidOutlinedIcon />}
                onClick={() => {
                  setFechaPago("");
                  setReferenciaPago("");
                  setPagoOpen(true);
                }}
                disabled={guardando}
                sx={btnSolid("#5E35B1", "#4527A0")}
              >
                Registrar pago
              </Button>
            )}
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimirActa}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir acta
            </Button>
            {vivo && puedeBorrar && (
              <Button
                startIcon={<BlockOutlinedIcon />}
                onClick={() => {
                  setRazonCancelar("");
                  setCancelarOpen(true);
                }}
                sx={btnOutline("#C62828")}
              >
                Cancelar solicitud
              </Button>
            )}
          </Stack>
        </Box>

        {/* Chasis */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
              Chasis ({lineas.length})
            </SectionTitle>
            {solicitud.estado === "borrador" && (
              <Button startIcon={<AddIcon />} onClick={abrirAgregar} sx={btnOutline(FP.color.navy)}>
                Agregar chasis
              </Button>
            )}
          </Box>
          <DataGrid
            autoHeight
            rows={lineas}
            columns={columnas}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={fpDataGridSx}
          />
        </Box>
      </Box>

      {/* Agregar chasis. El input es SOLO buscador (renderTags no pinta
          chips dentro): una solicitud real trae decenas de chasis y los
          chips reventaban el campo. La selección vive en el panel de abajo,
          con scroll propio. */}
      <Dialog open={agregarOpen} onClose={() => setAgregarOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Agregar chasis a la solicitud</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{ mt: 1 }}
            options={candidatos}
            loading={candidatosCargando}
            value={seleccion}
            onChange={(_, v) => setSeleccion(v)}
            filterOptions={filtrarCandidatos}
            getOptionLabel={(o) => o.chasis}
            isOptionEqualToValue={(o, v) => o.chasis === v.chasis}
            renderTags={() => null}
            renderOption={(props, o, { selected }) => (
              <li {...props} key={o.chasis}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {o.chasis}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {[o.marca, o.modelo, o.colore, o.suplidor].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  {selected && (
                    <TaskAltOutlinedIcon sx={{ fontSize: 18, color: FP.color.success }} />
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar por chasis, marca o modelo"
                helperText={
                  candidatosCargando
                    ? "Cargando candidatos…"
                    : `${candidatos.length} candidato(s): ${
                        solicitud.tipo === "dgii"
                          ? "vendidos con cobro de matrícula y sin placa recibida"
                          : "vendidos comprados a este proveedor y sin placa recibida"
                      }`
                }
                sx={fpInputSx}
              />
            )}
          />

          {/* Selección */}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.82rem", color: FP.color.textSecondary }}
            >
              {seleccion.length} seleccionado(s)
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => setSeleccion(candidatos)}
                disabled={candidatosCargando || candidatos.length === 0}
                sx={{ ...btnBase, color: FP.color.navy }}
              >
                Seleccionar todos ({candidatos.length})
              </Button>
              <Button
                size="small"
                onClick={() => setSeleccion([])}
                disabled={seleccion.length === 0}
                sx={{ ...btnBase, color: "#C62828" }}
              >
                Limpiar
              </Button>
            </Stack>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.7,
              alignContent: "flex-start",
              minHeight: 88,
              maxHeight: 220,
              overflowY: "auto",
              p: 1.2,
              border: `1px dashed ${FP.color.border}`,
              borderRadius: 1.8,
              bgcolor: FP.color.surface,
            }}
          >
            {seleccion.length === 0 ? (
              <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted, m: "auto" }}>
                Busca arriba y marca los chasis; aquí verás la selección.
              </Typography>
            ) : (
              seleccion.map((c) => (
                <Chip
                  key={c.chasis}
                  size="small"
                  label={c.chasis}
                  title={[c.marca, c.modelo, c.colore].filter(Boolean).join(" · ")}
                  onDelete={() => setSeleccion((prev) => prev.filter((x) => x.chasis !== c.chasis))}
                  sx={{
                    bgcolor: FP.color.cardBg,
                    border: `1px solid ${FP.color.border}`,
                    fontWeight: 700,
                    fontFamily: "monospace",
                  }}
                />
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgregarOpen(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={agregarChasis}
            disabled={guardando || seleccion.length === 0}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Agregar {seleccion.length > 0 ? `(${seleccion.length})` : ""}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registrar pago */}
      <Dialog open={pagoOpen} onClose={() => setPagoOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Registrar pago</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {esDgii && (
              <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                Total a cancelar: RD$ {fmtMoney(totalCancelar) || "—"}
              </Typography>
            )}
            <TextField
              fullWidth
              type="date"
              label="Fecha del pago (vacío = hoy)"
              value={fechaPago}
              onChange={(e) => setFechaPago(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
            <TextField
              fullWidth
              label="Referencia del pago"
              value={referenciaPago}
              onChange={(e) => setReferenciaPago(e.target.value)}
              sx={fpInputSx}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagoOpen(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const okPago = await ejecutar(
                "/api/solicitudmatricula_estado",
                { id, accion: "pagar", fechapago: fechaPago, referenciapago: referenciaPago },
                "Pago registrado"
              );
              if (okPago) setPagoOpen(false);
            }}
            disabled={guardando}
            sx={btnSolid("#5E35B1", "#4527A0")}
          >
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancelar solicitud */}
      <Dialog open={cancelarOpen} onClose={() => setCancelarOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancelar solicitud</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 1, ...fpInputSx }}
            label="Razón de la cancelación"
            value={razonCancelar}
            onChange={(e) => setRazonCancelar(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelarOpen(false)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const okCancelar = await ejecutar(
                "/api/solicitudmatricula_cancelar",
                { id, razon: razonCancelar },
                "Solicitud cancelada"
              );
              if (okCancelar) setCancelarOpen(false);
            }}
            disabled={guardando || !razonCancelar.trim()}
            sx={btnSolid("#C62828", "#B71C1C")}
          >
            Cancelar solicitud
          </Button>
        </DialogActions>
      </Dialog>

      {/* Observar línea */}
      <Dialog
        open={Boolean(observarLinea)}
        onClose={() => setObservarLinea(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Observar chasis {observarLinea?.chasis}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 1, ...fpInputSx }}
            label="Motivo de la observación (qué rebotó DGII)"
            value={motivoObservar}
            onChange={(e) => setMotivoObservar(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setObservarLinea(null)} sx={btnOutline(FP.color.navy)}>
            Cerrar
          </Button>
          <Button
            onClick={async () => {
              const okObservar = await ejecutar(
                "/api/solicitudmatricula_observar",
                { linea_id: observarLinea.id, motivo: motivoObservar },
                "Línea observada"
              );
              if (okObservar) setObservarLinea(null);
            }}
            disabled={guardando || !motivoObservar.trim()}
            sx={btnSolid("#C62828", "#B71C1C")}
          >
            Observar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

SolicitudMatricula.propTypes = {
  id: PropTypes.number.isRequired,
  onExit: PropTypes.func,
  onChanged: PropTypes.func,
};

SolicitudMatricula.defaultProps = {
  onExit: null,
  onChanged: null,
};
